import { Fragment, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/useEntity';
import { fmt } from '../../lib/formatters';
import { Spinner } from '../../components/ui/Spinner';
import type { ResumenRappel, TrimestreRappel } from '../../../shared/rappel';

/**
 * Pestaña «Rappel» del análisis (CEO, 22/10/2026): el rappel que APPROX tiene
 * que pagar al cliente cada trimestre, sobre lo facturado (o entregado, si el
 * cliente no tiene la facturación del ERP importada) y con el % de la
 * condición comercial de cada producto. Reglas en shared/rappel.ts.
 * Datos: GET /api/rappel?cliente=…&anio=…&fuente=…
 */

type Respuesta = ResumenRappel & {
  cliente: { codigo: string; nombre: string };
  fuente: 'erp' | 'pedidos' | null;
  fuentes: ('erp' | 'pedidos')[];
  datos_hasta: string | null;
  anios: number[];
  hoy: string;
};

const FUENTE: Record<'erp' | 'pedidos', { label: string; base: string }> = {
  erp: { label: 'Facturación APPROX', base: 'el total facturado sin impuestos (export del ERP de APPROX: importe tras el descuento técnico), con los abonos restando' },
  pedidos: { label: 'Pedidos entregados', base: 'lo entregado de los pedidos confirmados, sin impuestos, en la fecha de entrega (la misma regla que la comisión)' },
};

const fechaHasta = (v: string | null) => (v == null ? '—' : /^\d{4}-\d{2}$/.test(v) ? `${v.slice(5, 7)}/${v.slice(0, 4)}` : fmt.date(v));

export function RappelCliente({ cliente }: { cliente: string }) {
  const [anio, setAnio] = useState<number | null>(null);
  const [fuente, setFuente] = useState<'erp' | 'pedidos' | null>(null);
  const [abierto, setAbierto] = useState<number | null>(null);

  const qs = new URLSearchParams({ cliente });
  if (anio) qs.set('anio', String(anio));
  if (fuente) qs.set('fuente', fuente);
  const q = useQuery({
    queryKey: ['rappel', qs.toString()],
    queryFn: () => api.get<Respuesta>(`/rappel?${qs.toString()}`),
    staleTime: 60_000,
  });
  const d = q.data;

  if (q.isLoading) return <div className="py-10"><Spinner /></div>;
  if (q.error) return <p role="alert" className="rounded-md bg-bad-50 px-3 py-2 text-sm text-bad-700">No se pudo calcular el rappel. {(q.error as Error).message}</p>;
  if (!d) return null;
  if (!d.fuente) {
    return <p className="rounded-lg border border-slate-200 bg-white px-5 py-8 text-center text-sm text-slate-500">{cliente} no tiene facturación ni pedidos entregados: no hay rappel que calcular.</p>;
  }
  const conEstimado = d.fuente === 'pedidos';

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-sm">
        <Grupo label="Año">
          {d.anios.map((a) => <Pildora key={a} activo={d.anio === a} onClick={() => setAnio(a)}>{a}</Pildora>)}
        </Grupo>
        {d.fuentes.length > 1 && (
          <Grupo label="Base">
            {d.fuentes.map((f) => <Pildora key={f} activo={d.fuente === f} onClick={() => setFuente(f)}>{FUENTE[f].label}</Pildora>)}
          </Grupo>
        )}
        <span className="ml-auto text-xs text-slate-500">Datos hasta {fechaHasta(d.datos_hasta)}</span>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi titulo={`Rappel ${d.anio}`} valor={fmt.eur(d.total.rappel)} nota="devengado en el año" fuerte />
        <Kpi titulo="Base del rappel" valor={fmt.eur(d.total.base)} nota={d.fuente === 'erp' ? 'total facturado sin impuestos' : 'entregado sin impuestos'} />
        {d.fuente === 'erp'
          ? <Kpi titulo="Descuento técnico" valor={fmt.eur(d.total.dto_tecnico)} nota={`sobre ${fmt.eur(d.total.base_bruta)} facturados`} />
          : <Kpi titulo="Previsto pendiente" valor={fmt.eur(d.total.estimado_rappel)} nota={`sobre ${fmt.eur(d.total.estimado_base)} por entregar`} />}
        <Kpi titulo="Sin condición comercial" valor={fmt.eur(d.total.base_sin_condicion)} nota="no genera rappel: revisar" aviso={d.total.base_sin_condicion !== 0} />
      </div>

      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-2">Trimestre</th>
              <th className="px-4 py-2">Periodo</th>
              {d.fuente === 'erp' && <th className="px-4 py-2 text-right">Facturado</th>}
              {d.fuente === 'erp' && <th className="px-4 py-2 text-right">Dto. técnico</th>}
              <th className="px-4 py-2 text-right">Base</th>
              <th className="px-4 py-2 text-right">Rappel a pagar</th>
              {conEstimado && <th className="px-4 py-2 text-right">Previsto (pendiente)</th>}
              <th className="px-4 py-2">Estado</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {d.trimestres.map((t) => (
              <Fragment key={t.trimestre}>
                <tr className={`cursor-pointer hover:bg-slate-50 ${abierto === t.trimestre ? 'bg-slate-50' : ''}`}
                  onClick={() => setAbierto(abierto === t.trimestre ? null : t.trimestre)}
                  aria-expanded={abierto === t.trimestre}>
                  <td className="px-4 py-2 font-semibold">
                    <span className="mr-1 inline-block w-3 text-slate-400">{t.tramos.length ? (abierto === t.trimestre ? '▾' : '▸') : ''}</span>
                    T{t.trimestre} {d.anio}
                  </td>
                  <td className="px-4 py-2 text-slate-500">{fmt.date(t.desde)} – {fmt.date(t.hasta)}</td>
                  {d.fuente === 'erp' && <td className="px-4 py-2 text-right tabular-nums">{fmt.eur(t.base_bruta)}</td>}
                  {d.fuente === 'erp' && <td className="px-4 py-2 text-right tabular-nums text-slate-500">{t.dto_tecnico ? `−${fmt.eur(t.dto_tecnico)}` : '—'}</td>}
                  <td className="px-4 py-2 text-right tabular-nums">{fmt.eur(t.base)}</td>
                  <td className="px-4 py-2 text-right font-bold tabular-nums text-slate-900">{fmt.eur(t.rappel)}</td>
                  {conEstimado && <td className="px-4 py-2 text-right tabular-nums text-slate-500">{t.estimado_rappel ? fmt.eur(t.estimado_rappel) : '—'}</td>}
                  <td className="px-4 py-2"><Estado t={t} hoy={d.hoy} /></td>
                </tr>
                {abierto === t.trimestre && <Tramos t={t} columnas={d.fuente === 'erp' ? 7 : 6} />}
              </Fragment>
            ))}
          </tbody>
          <tfoot className="border-t-2 border-slate-200 bg-slate-50 font-semibold">
            <tr>
              <td className="px-4 py-2" colSpan={2}>Total {d.anio}</td>
              {d.fuente === 'erp' && <td className="px-4 py-2 text-right tabular-nums">{fmt.eur(d.total.base_bruta)}</td>}
              {d.fuente === 'erp' && <td className="px-4 py-2 text-right tabular-nums text-slate-500">{d.total.dto_tecnico ? `−${fmt.eur(d.total.dto_tecnico)}` : '—'}</td>}
              <td className="px-4 py-2 text-right tabular-nums">{fmt.eur(d.total.base)}</td>
              <td className="px-4 py-2 text-right tabular-nums text-slate-900">{fmt.eur(d.total.rappel)}</td>
              {conEstimado && <td className="px-4 py-2 text-right tabular-nums text-slate-500">{fmt.eur(d.total.estimado_rappel)}</td>}
              <td />
            </tr>
          </tfoot>
        </table>
      </div>

      <p className="text-xs leading-relaxed text-slate-500">
        Base: {FUENTE[d.fuente].base}. El % es el rappel de la condición comercial más específica de cada producto
        (marca › gama › categoría › subcategoría). Trimestres naturales por fecha de factura o entrega.
        {d.sin_fecha_base ? ` Hay ${fmt.eur(d.sin_fecha_base)} entregados sin fecha que no entran en ningún trimestre.` : ''}
        {' '}Pulsa un trimestre para ver el desglose por condición.
      </p>
    </div>
  );
}

function Tramos({ t, columnas }: { t: TrimestreRappel; columnas: number }) {
  return (
    <tr className="bg-slate-50/60">
      <td colSpan={columnas} className="px-4 pb-3 pt-1">
        <table className="ml-6 w-auto text-xs">
          <thead className="text-left text-slate-500">
            <tr><th className="py-1 pr-6 font-semibold">Condición comercial</th><th className="py-1 pr-6 text-right font-semibold">%</th><th className="py-1 pr-6 text-right font-semibold">Base</th><th className="py-1 text-right font-semibold">Rappel</th></tr>
          </thead>
          <tbody>
            {t.tramos.map((x) => (
              <tr key={`${x.condicion}|${x.pct}`}>
                <td className="py-0.5 pr-6">{x.condicion || '—'}</td>
                <td className="py-0.5 pr-6 text-right tabular-nums">{fmt.number(x.pct)} %</td>
                <td className="py-0.5 pr-6 text-right tabular-nums">{fmt.eur(x.base)}</td>
                <td className="py-0.5 text-right tabular-nums font-semibold">{fmt.eur(x.rappel)}</td>
              </tr>
            ))}
            {t.base_sin_condicion !== 0 && (
              <tr className="text-warn-700">
                <td className="py-0.5 pr-6">Sin condición comercial</td><td className="py-0.5 pr-6 text-right">—</td>
                <td className="py-0.5 pr-6 text-right tabular-nums">{fmt.eur(t.base_sin_condicion)}</td><td className="py-0.5 text-right">—</td>
              </tr>
            )}
          </tbody>
        </table>
      </td>
    </tr>
  );
}

function Estado({ t, hoy }: { t: TrimestreRappel; hoy: string }) {
  if (t.cerrado) return <span className="rounded bg-ok-50 px-2 py-0.5 text-xs font-semibold text-ok-700">Cerrado · a liquidar</span>;
  if (hoy >= t.desde) return <span className="rounded bg-warn-50 px-2 py-0.5 text-xs font-semibold text-warn-700">En curso</span>;
  return <span className="text-xs text-slate-400">Pendiente</span>;
}

function Kpi({ titulo, valor, nota, fuerte, aviso }: { titulo: string; valor: string; nota: string; fuerte?: boolean; aviso?: boolean }) {
  return (
    <div className={`rounded-lg border bg-white px-4 py-3 shadow-sm ${aviso ? 'border-warn-300' : 'border-slate-200'}`}>
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{titulo}</div>
      <div className={`mt-1 font-display tabular-nums ${fuerte ? 'text-2xl font-extrabold text-slate-900' : 'text-xl font-bold text-slate-800'}`}>{valor}</div>
      <div className={`mt-0.5 text-xs ${aviso ? 'text-warn-700' : 'text-slate-500'}`}>{nota}</div>
    </div>
  );
}

function Grupo({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</span>
      {children}
    </div>
  );
}

function Pildora({ activo, onClick, children }: { activo: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={activo}
      className={`cursor-pointer rounded-md border px-2.5 py-1 text-xs font-semibold transition ${activo ? 'border-accent-600 bg-accent-600 text-white' : 'border-slate-300 bg-white text-slate-600 hover:border-accent-300 hover:text-slate-900'}`}>
      {children}
    </button>
  );
}
